import React, { useEffect } from 'react'
import {
  Button,
  Modal,
  Box,
  useMediaQuery,
  Typography,
  Grid,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material'
import { CarnivoreModalProps } from '../../_types/modal-props.interface'
import { StyleHelper } from '../style-helper'
import LoadingButton from '../controls/loading-btn'
import { CarnivoreAlert } from '../../_types/alert.type'
import ThemeSwitch from '../theme-switch'
import {
  ArrowOutwardRounded,
  BookmarkAdd,
  BookmarkRemove,
  Campaign,
  Close,
  Insights
} from '@mui/icons-material'
import AlertHTMLContent from '../alert-html-content'
import { AlertService } from '../../services/alert.service'
import { useError } from '../../context/error'
import { useGlobalState } from '../../context/global'
import useBoolean from '../../hooks/useBoolean'

interface ViewAlertModalProps extends CarnivoreModalProps {
  alert: CarnivoreAlert | null
}

const ViewAlertModal: React.FC<ViewAlertModalProps> = ({
  open,
  onClose,
  size = 'lg',
  alert
}) => {
  const isSmallScreen = useMediaQuery('(max-width:600px)')
  const { setError } = useError()
  const { addToViewAlerts, user, setUser } = useGlobalState()
  const alertService = new AlertService(setError)

  const [bookmarkLoading, { toggle: toggleBMLoading }] = useBoolean(false)

  useEffect(() => {
    if (!open || !alert?.id) return
    alertService.viewAlert(alert.id).then(() => {
      addToViewAlerts([{ userID: user?.id ?? '', alertID: alert.id }])
    })
  }, [open, alert?.id])

  const _getWidth = () => {
    if (isSmallScreen) {
      return '90%'
    }
    switch (size) {
      case 'sm':
        return '30%'
      case 'md':
        return '60%'
      case 'lg':
        return '80%'
    }
  }

  const _formateTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return `${date.toLocaleDateString('en-US', {
      dateStyle: 'full'
    })} at ${date.toLocaleTimeString('en-US', { timeStyle: 'short' })}`
  }

  const _bookmarkAlert = async () => {
    toggleBMLoading()
    const res = await alertService.bookmarkAlert(alert?.id ?? '')
    if (res.user) {
      setUser(res.user)
    }
    toggleBMLoading()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box
        sx={{
          position: 'fixed',
          top: '50svh',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: _getWidth(),
          maxWidth: '100%',
          maxHeight: '90dvh', // prevent overflow on mobile
          overflowY: 'scroll',
          bgcolor: `background.paper`,
          border: `2px solid #000`,
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Grid container alignItems='center' justifyContent={'center'}>
          <Grid item xs={12} md={8} sx={{ textAlign: 'left' }}>
            <Box display='flex'>
              {alert?.alertType === 'alert' ? (
                <Campaign color='primary' sx={{ fontSize: 36, mr: 2 }} />
              ) : (
                <Insights color='primary' sx={{ fontSize: 36, mr: 2 }} />
              )}
              <Typography color={'primary'} variant='h5' fontWeight={'bold'}>
                {alert?.subject}
              </Typography>
            </Box>

            {/* Adjust margin */}
          </Grid>
          <Grid item md={4} xs={12} sx={{ textAlign: 'right' }} alignItems={'flex-end'}>
            <ThemeSwitch />

            <Tooltip
              title={`${user?.bookmarkedAlerts?.includes(alert?.id ?? '')
                  ? 'Remove from'
                  : 'Add to'
                } bookmarks`}
            >
              <IconButton onClick={_bookmarkAlert} disabled={bookmarkLoading}>
                {bookmarkLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    {!user?.bookmarkedAlerts?.includes(alert?.id ?? '') ? (
                      <BookmarkAdd />
                    ) : (
                      <BookmarkRemove />
                    )}
                  </>
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title='Open in new tab'>
              <IconButton
                href={`/alerts/${alert?.id}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <ArrowOutwardRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close'>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Tooltip>

            {/* Adjust margin */}
          </Grid>
        </Grid>

        <StyleHelper.VerticalSpace />
        <Typography variant='body2' sx={{ ml: 1 }}>
          {_formateTime(alert?.timeStamp ?? 0)}
        </Typography>
        <StyleHelper.VerticalSpace />

        <AlertHTMLContent html={alert?.contentHTML} />
   
        <StyleHelper.DoubleVerticalSpace />
        <Grid container justifyContent='flex-end' alignItems='flex-end'>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <Button variant='text' sx={{ ml: 1 }} onClick={onClose}>
              Close
            </Button>
            {/* Adjust margin */}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  )
}

export default ViewAlertModal
